import { MapPin, Clock, Calendar, Linkedin, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ContactForm } from '../components/forms/ContactForm';
import { SEO } from '../components/SEO';

const CALENDLY_URL = "https://calendly.com/siddharthkaul";

export default function ContactPage() {
  const availability = [
    { type: 'Currently accepting', items: ['Strategic advisory (1-2 weeks lead time)', 'Speaking engagements (Q1 2026 onwards)'] },
    { type: 'Limited availability', items: ['Implementation partnerships (Q2 2026)'] },
    { type: 'Waitlist', items: ['Ongoing advisory retainers'] },
  ];

  return (
    <main className="pt-20" data-testid="contact-page">
      <SEO
        title="Contact Siddharth Kaul | Book Cloud Consulting Discovery Call"
        description="Get in touch to discuss cloud infrastructure consulting, AI/ML platform architecture, or speaking opportunities. Book a 30-minute discovery call or send a message."
        keywords="contact cloud consultant, book consulting call, cloud infrastructure inquiry, speaking engagement request"
        canonicalUrl="/contact"
      />
      {/* Header */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              Contact
            </p>
            <h1 className="font-['Montserrat'] font-extrabold text-4xl md:text-5xl text-[#2C3E50] mb-4">
              Get in Touch
            </h1>
            <p className="text-lg text-[#64748B] leading-relaxed">
              Exploring projects or have a question? I'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Calendly Booking Section */}
      <section className="py-12 bg-[#F8F9FA]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#3498DB]/10 flex items-center justify-center">
                  <Calendar className="text-[#3498DB]" size={20} />
                </div>
                <div>
                  <h2 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50]">
                    Book a Discovery Call
                  </h2>
                  <p className="text-sm text-[#64748B]">30 minutes, free - Let's discuss your challenges</p>
                </div>
              </div>
            </div>
            {/* Calendly Inline Widget */}
            <div className="calendly-inline-widget" data-url={`${CALENDLY_URL}?hide_gdpr_banner=1`} style={{ minWidth: '320px', height: '700px' }} data-testid="calendly-widget">
              <iframe
                src={`${CALENDLY_URL}?embed_domain=siddharthkaul.com&embed_type=Inline&hide_gdpr_banner=1`}
                width="100%"
                height="100%"
                frameBorder="0"
                title="Schedule a call with Siddharth Kaul"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Options */}
      <section className="py-12 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#F8F9FA] rounded-xl p-8">
                <h2 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50] mb-2">
                  Or Send a Message
                </h2>
                <p className="text-[#64748B] text-sm mb-6">
                  I'll respond within 24-48 hours
                </p>
                <ContactForm />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Options */}
              <div className="bg-[#F8F9FA] rounded-xl p-6">
                <h3 className="font-['Montserrat'] font-semibold text-lg text-[#2C3E50] mb-4">
                  Other Ways to Connect
                </h3>
                <div className="space-y-4">
                  {/* LinkedIn */}
                  <a
                    href="https://linkedin.com/in/siddharth-k-8b41b619"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-[#3498DB] transition-colors duration-200"
                    data-testid="contact-linkedin"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#0077B5]/10 flex items-center justify-center">
                      <Linkedin className="text-[#0077B5]" size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-[#2C3E50]">LinkedIn</p>
                      <p className="text-sm text-[#64748B]">Connect & follow</p>
                    </div>
                  </a>

                  {/* Substack */}
                  <a
                    href="https://siddharthkaul.substack.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-[#3498DB] transition-colors duration-200"
                    data-testid="contact-substack"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#FF6719]/10 flex items-center justify-center">
                      <FileText className="text-[#FF6719]" size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-[#2C3E50]">Newsletter</p>
                      <p className="text-sm text-[#64748B]">Subscribe on Substack</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Location & Availability */}
              <div className="bg-[#F8F9FA] rounded-xl p-6">
                <h3 className="font-['Montserrat'] font-semibold text-lg text-[#2C3E50] mb-4">
                  Location & Availability
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="text-[#3498DB] mt-1" size={18} />
                    <div>
                      <p className="text-[#2C3E50] font-medium">Based Globally</p>
                      <p className="text-sm text-[#64748B]">Work with clients worldwide (remote + travel)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="text-[#3498DB] mt-1" size={18} />
                    <div>
                      <p className="text-[#2C3E50] font-medium">Response Time</p>
                      <p className="text-sm text-[#64748B]">Consulting: 24-48 hours</p>
                      <p className="text-sm text-[#64748B]">Speaking: 1 week</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Availability Status */}
              <div className="bg-[#2C3E50] rounded-xl p-6 text-white">
                <h3 className="font-['Montserrat'] font-semibold text-lg mb-4">
                  Current Availability
                </h3>
                <div className="space-y-4">
                  {availability.map((item, index) => (
                    <div key={index}>
                      <p className="text-[#3498DB] text-sm font-medium mb-1">{item.type}</p>
                      <ul className="space-y-1">
                        {item.items.map((i, idx) => (
                          <li key={idx} className="text-slate-300 text-sm">
                            • {i}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Speaking & Media */}
      <section className="py-16 bg-[#F8F9FA]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="bg-white rounded-xl p-8 md:p-12 border border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50] mb-4">
                  For Speaking & Media
                </h3>
                <p className="text-[#64748B] mb-4">
                  Available for keynotes, panels, workshops, podcasts, and media commentary.
                </p>
                <p className="text-[#64748B] text-sm">
                  Please use the contact form or book a call to discuss speaking opportunities.
                </p>
              </div>
              <div className="text-center md:text-right">
                <Button
                  asChild
                  className="bg-[#3498DB] hover:bg-[#2980B9] text-white px-8 py-6"
                >
                  <a href="https://calendly.com/siddharthkaul" target="_blank" rel="noopener noreferrer">
                    <Calendar size={18} className="mr-2" />
                    Book a Call
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
